import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { FlowDefinition } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, PageHeader, Spinner, StatusBadge } from '../../ui/components';

interface DslState {
  slaHours?: number;
  assigneeRole?: string;
  terminal?: boolean;
  checklists?: Array<{ code: string; label: string }>;
  transitions?: Array<{ action: string; to: string; roles: string[] }>;
}
interface Dsl {
  refPrefix: string;
  initial: string;
  states: Record<string, DslState>;
}

export function FlowDefinitionDetail() {
  const { code } = useParams<{ code: string }>();
  const { persona } = useAuth();
  const canAdmin = persona?.roles.includes('FLOW_ADMIN') ?? false;
  const versionsQ = useApiQuery<{ items: FlowDefinition[] }>(code ? `/api/v1/flow/definitions?code=${code}&limit=100` : null);
  const [selected, setSelected] = useState<number | null>(null);

  const versions = versionsQ.data?.items ?? [];
  const current = versions.find((v) => v.version === selected) ?? versions[0];
  const dsl = current?.dsl as Dsl | undefined;

  if (versionsQ.loading) return <Spinner />;
  if (versionsQ.error) return <ErrorNote message={versionsQ.error} />;
  if (!current) return <EmptyState title="Definition not found" hint={code} />;

  return (
    <div>
      <PageHeader
        title={current.code}
        subtitle={`Module ${current.moduleCode} — ${versions.length} version${versions.length === 1 ? '' : 's'}`}
        actions={
          <select
            className="field-input max-w-[140px]"
            value={current.version}
            onChange={(e) => setSelected(Number.parseInt(e.target.value, 10))}
          >
            {versions
              .slice()
              .sort((a, b) => b.version - a.version)
              .map((v) => (
                <option key={v.id} value={v.version}>
                  v{v.version} {v.active ? '(active)' : ''}
                </option>
              ))}
          </select>
        }
      />
      <div className="mb-3">
        <StatusBadge status={current.active ? 'ACTIVE' : 'INACTIVE'} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="card p-4">
          <h3 className="mb-3 text-sm font-bold text-slate-700">States &amp; transitions</h3>
          {dsl ? (
            <div className="space-y-3">
              {Object.entries(dsl.states).map(([name, state]) => (
                <div key={name} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{name}</span>
                    {name === dsl.initial ? <StatusBadge status="OPEN" /> : null}
                    {state.terminal ? <StatusBadge status="CLOSED" /> : null}
                  </div>
                  {state.slaHours ? (
                    <div className="mt-1 text-[11px] text-slate-400">
                      SLA {state.slaHours}h · role {state.assigneeRole}
                    </div>
                  ) : null}
                  {(state.checklists?.length ?? 0) > 0 ? (
                    <div className="mt-1 text-[11px] text-slate-500">
                      Checklist: {state.checklists!.map((cl) => cl.label).join(', ')}
                    </div>
                  ) : null}
                  {(state.transitions?.length ?? 0) > 0 ? (
                    <ul className="mt-2 space-y-1 text-xs">
                      {state.transitions!.map((t) => (
                        <li key={t.action} className="text-slate-600">
                          <span className="font-mono">{t.action}</span> → <span className="font-mono">{t.to}</span>{' '}
                          <span className="text-slate-400">({t.roles.join(', ')})</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400">No DSL to display.</p>
          )}
          <details className="mt-4">
            <summary className="cursor-pointer text-xs font-semibold text-slate-500">Raw DSL JSON</summary>
            <pre className="mt-2 max-h-96 overflow-auto rounded-lg bg-slate-50 p-3 text-[11px]">
              {JSON.stringify(current.dsl, null, 2)}
            </pre>
          </details>
        </div>

        {canAdmin ? (
          <SimulatePanel code={current.code} />
        ) : (
          <div className="card p-4">
            <h3 className="text-sm font-bold text-slate-700">Simulate</h3>
            <p className="mt-2 text-xs text-slate-400">Simulating a definition needs the FLOW_ADMIN role.</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SimulatePanel({ code }: { code: string }) {
  const [subjectJson, setSubjectJson] = useState('{}');
  const [steps, setSteps] = useState<Array<{ action: string; payload: string }>>([{ action: '', payload: '{}' }]);
  const [result, setResult] = useState<{ path: unknown[]; finalState: string; completed: boolean } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const subject = JSON.parse(subjectJson || '{}');
      const parsedSteps = steps
        .filter((s) => s.action.trim())
        .map((s) => ({ action: s.action.trim(), payload: JSON.parse(s.payload || '{}') }));
      const r = await api.post<{ path: unknown[]; finalState: string; completed: boolean }>(
        `/api/v1/flow/definitions/${code}/simulate`,
        { subject, steps: parsedSteps },
      );
      setResult(r);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Simulation failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card p-4">
      <h3 className="mb-3 text-sm font-bold text-slate-700">Simulate</h3>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <Field label="Subject JSON">
        <textarea
          className="field-input font-mono text-xs"
          rows={2}
          value={subjectJson}
          onChange={(e) => setSubjectJson(e.target.value)}
        />
      </Field>
      <div className="mt-3 space-y-2">
        {steps.map((s, i) => (
          <div key={i} className="flex gap-2">
            <input
              className="field-input"
              placeholder="action, e.g. start_review"
              value={s.action}
              onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, action: e.target.value } : x)))}
            />
            <input
              className="field-input font-mono text-xs"
              placeholder="payload JSON"
              value={s.payload}
              onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, payload: e.target.value } : x)))}
            />
            <button
              className="btn-secondary !px-2 text-xs"
              onClick={() => setSteps(steps.filter((_, j) => j !== i))}
              disabled={steps.length === 1}
            >
              ✕
            </button>
          </div>
        ))}
        <button className="btn-secondary text-xs" onClick={() => setSteps([...steps, { action: '', payload: '{}' }])}>
          + Add step
        </button>
      </div>
      <button className="btn-primary mt-3" onClick={run} disabled={busy}>
        {busy ? 'Running…' : 'Run simulation'}
      </button>

      {result ? (
        <div className="mt-4 rounded-lg border border-slate-200 p-3">
          <div className="text-xs">
            Final state: <span className="font-mono font-semibold">{result.finalState}</span>{' '}
            <StatusBadge status={result.completed ? 'DONE' : 'OPEN'} />
          </div>
          <pre className="mt-2 max-h-64 overflow-auto rounded bg-slate-50 p-2 text-[11px]">
            {JSON.stringify(result.path, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  );
}
